import { useState, useEffect } from 'react';
import { User, Mail, Phone, Shield, Save } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { USER_TYPES } from '../../utils/constants';
import { roleService, userService } from '../../services';
import type { Role } from '../../services';

interface UserData {
  id: string;
  username: string;
  email: string;
  userRealName: string;
  phone: string;
  type: number; // 1=SuperAdmin, 2=TenantAdmin
  status: number; // 0=Active, 1=Disabled
  tenantId?: string;
  roleId?: string; // Current role assignment
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (user: UserData) => void;
  user?: UserData;
  mode: 'add' | 'edit';
}

const UserModal = ({ isOpen, onClose, onSave, user, mode }: UserModalProps) => {
  const toast = useToast();
  const { user: currentUser } = useAuthStore();
  const { selectedTenantId } = useTenantStore();
  const isSuperAdmin = currentUser?.type === USER_TYPES.SUPER_ADMIN;

  const [formData, setFormData] = useState<UserData>(
    user || {
      id: '',
      username: '',
      email: '',
      userRealName: '',
      phone: '',
      type: 2, // Default to TenantAdmin
      status: 0, // Default to Active
      tenantId: currentUser?.tenantId ?? undefined,
    }
  );
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [currentRoleId, setCurrentRoleId] = useState<string>('');

  useEffect(() => {
    if (user) {
      setFormData(user);
    }
  }, [user]);

  // Fetch available roles and current user role when editing a TenantAdmin
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    if (user.type !== USER_TYPES.TENANT_ADMIN) return;

    const fetchRolesData = async () => {
      try {
        // Filter roles by tenant: use the edited user's tenant, or the selected tenant from the top bar
        const tenantId = user.tenantId || selectedTenantId || undefined;
        const [rolesResp, userRoles] = await Promise.all([
          roleService.list({ pageSize: 100, tenantId }),
          userService.getUserRoles(user.id),
        ]);
        setAvailableRoles(rolesResp.items || []);
        const fetchedRoleId = userRoles.length > 0 ? (userRoles[0].roleId || '') : '';
        setCurrentRoleId(fetchedRoleId);
        // Sync into formData so change detection in handleSubmit works correctly
        setFormData((prev) => ({ ...prev, roleId: fetchedRoleId }));
      } catch {
        // Non-critical — roles just won't show
      }
    };
    fetchRolesData();
  }, [isOpen, user?.id, user?.type]);

  const handleChange = (field: keyof UserData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.email || !formData.userRealName) {
      toast.error('Validation Error', 'Please fill in all required fields');
      return;
    }

    // Email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Validation Error', 'Please enter a valid email address');
      return;
    }

    // Handle role assignment change for TenantAdmins in edit mode
    if (mode === 'edit' && user?.id && user.type === USER_TYPES.TENANT_ADMIN && formData.roleId !== currentRoleId) {
      try {
        // Unassign old role if exists
        if (currentRoleId) {
          await userService.unassignRole(user.id, currentRoleId);
        }
        // Assign new role if selected
        if (formData.roleId) {
          await userService.assignRole(user.id, formData.roleId);
        }
      } catch (err: any) {
        toast.error('Role Assignment Failed', err.message || 'Failed to update role');
      }
    }

    onSave(formData);
    onClose();
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button onClick={handleSubmit} className="btn-primary inline-flex items-center space-x-2">
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create User' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add New User' : `Edit User: ${user?.username}`}
      size="2xl"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <User className="w-5 h-5 text-primary-600" />
            <span>Basic Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>Email <span className="text-red-500">*</span></span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => {
                  handleChange('email', e.target.value);
                  // Auto-populate username from email if in add mode
                  if (mode === 'add') {
                    handleChange('username', e.target.value);
                  }
                }}
                placeholder="e.g., john.doe@example.com"
                className="input-base w-full"
                disabled={mode === 'edit'} // Email cannot be changed after creation
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.userRealName}
                onChange={(e) => handleChange('userRealName', e.target.value)}
                placeholder="e.g., John Doe"
                className="input-base w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>Phone</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="e.g., +1 (555) 123-4567"
                className="input-base w-full"
              />
            </div>
          </div>
        </div>

        {/* Role & Status */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <Shield className="w-5 h-5 text-primary-600" />
            <span>Role & Access</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', parseInt(e.target.value))}
                className="input-base w-full"
                disabled={!isSuperAdmin} // Only super admin can change user types
                required
              >
                <option value={2}>Tenant Admin</option>
                {isSuperAdmin && <option value={1}>Super Admin</option>}
              </select>
              {!isSuperAdmin && (
                <p className="text-xs text-gray-500 mt-1">
                  Only Super Admins can create other Super Admin users.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', parseInt(e.target.value))}
                className="input-base w-full"
                required
              >
                <option value={0}>Active</option>
                <option value={1}>Disabled</option>
              </select>
            </div>

            {/* Role assignment - only for TenantAdmins in edit mode */}
            {mode === 'edit' && formData.type === USER_TYPES.TENANT_ADMIN && availableRoles.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Role
                </label>
                <select
                  value={formData.roleId || currentRoleId || ''}
                  onChange={(e) => handleChange('roleId', e.target.value)}
                  className="input-base w-full"
                >
                  <option value="">No role assigned</option>
                  {availableRoles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name || r.roleName} ({r.role_type || 'Custom'})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Roles determine what this user can do within the tenant.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Info Note - Keycloak handles authentication */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> User authentication is managed through Keycloak. Users will receive an invitation email to set up their credentials.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default UserModal;
